// Cyberpunk Background Effects
// Creates animated particle/grid canvas background

// Export class for consumption in app.js
export default class CyberBackground {
    constructor() {
        this.canvas = document.getElementById('cyber-canvas');
        if (!this.canvas) return;

        this.ctx = this.canvas.getContext('2d');
        this.particles = [];
        this.gridLines = [];
        this.mouseX = 0;
        this.mouseY = 0;

        this.colors = {
            cyan: '#00f5ff',
            magenta: '#ff00ff',
            purple: '#7c3aed',
            green: '#00ff88'
        };

        this.init();
        this.animate();
        this.handleResize();
        this.handleMouse();
    }

    init() {
        this.resize();
        this.createParticles();
        this.createGridLines();
    }

    resize() {
        this.width = this.canvas.width = window.innerWidth;
        this.height = this.canvas.height = window.innerHeight;
    }

    handleResize() {
        window.addEventListener('resize', () => {
            this.resize();
            this.createParticles();
            this.createGridLines();
        });
    }

    handleMouse() {
        document.addEventListener('mousemove', (e) => {
            this.mouseX = e.clientX;
            this.mouseY = e.clientY;
        });
    }

    createParticles() {
        this.particles = [];
        const particleCount = Math.floor((this.width * this.height) / 15000);

        for (let i = 0; i < particleCount; i++) {
            this.particles.push({
                x: Math.random() * this.width,
                y: Math.random() * this.height,
                size: Math.random() * 2 + 0.5,
                speedX: (Math.random() - 0.5) * 0.5,
                speedY: (Math.random() - 0.5) * 0.5,
                color: Object.values(this.colors)[Math.floor(Math.random() * 4)],
                opacity: Math.random() * 0.5 + 0.2
            });
        }
    }

    createGridLines() {
        this.gridLines = [];
        const spacing = 80;

        // Vertical lines
        for (let x = 0; x < this.width; x += spacing) {
            this.gridLines.push({
                x1: x,
                y1: 0,
                x2: x,
                y2: this.height,
                type: 'vertical'
            });
        }

        // Horizontal lines
        for (let y = 0; y < this.height; y += spacing) {
            this.gridLines.push({
                x1: 0,
                y1: y,
                x2: this.width,
                y2: y,
                type: 'horizontal'
            });
        }
    }

    drawGrid() {
        const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
        const opacity = isDark ? 0.08 : 0.05;

        this.ctx.strokeStyle = isDark ? `rgba(0, 245, 255, ${opacity})` : `rgba(37, 99, 235, ${opacity})`;
        this.ctx.lineWidth = 1;

        this.gridLines.forEach(line => {
            this.ctx.beginPath();
            this.ctx.moveTo(line.x1, line.y1);
            this.ctx.lineTo(line.x2, line.y2);
            this.ctx.stroke();
        });
    }

    drawParticles() {
        const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

        this.particles.forEach(particle => {
            // Update position
            particle.x += particle.speedX;
            particle.y += particle.speedY;

            // Wrap around screen
            if (particle.x < 0) particle.x = this.width;
            if (particle.x > this.width) particle.x = 0;
            if (particle.y < 0) particle.y = this.height;
            if (particle.y > this.height) particle.y = 0;

            // Mouse interaction - subtle attraction
            const dx = this.mouseX - particle.x;
            const dy = this.mouseY - particle.y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < 200) {
                particle.x += dx * 0.002;
                particle.y += dy * 0.002;
            }

            // Draw particle
            const opacity = isDark ? particle.opacity : particle.opacity * 0.7;
            this.ctx.beginPath();
            this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
            this.ctx.fillStyle = isDark ? particle.color : '#2563eb';
            this.ctx.globalAlpha = opacity;
            this.ctx.fill();
            this.ctx.globalAlpha = 1;

            // Draw glow effect (both modes, stronger in dark)
            if (particle.size > 1.5) {
                this.ctx.beginPath();
                this.ctx.arc(particle.x, particle.y, particle.size * 3, 0, Math.PI * 2);
                const gradient = this.ctx.createRadialGradient(
                    particle.x, particle.y, 0,
                    particle.x, particle.y, particle.size * 3
                );
                if (isDark) {
                    gradient.addColorStop(0, `${particle.color}33`);
                } else {
                    gradient.addColorStop(0, 'rgba(37, 99, 235, 0.15)');
                }
                gradient.addColorStop(1, 'transparent');
                this.ctx.fillStyle = gradient;
                this.ctx.fill();
            }
        });
    }

    drawConnections() {
        const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
        const connectionDistance = isDark ? 120 : 100;
        const baseOpacity = isDark ? 0.15 : 0.08;
        const strokeColor = isDark ? '0, 245, 255' : '37, 99, 235';

        this.particles.forEach((p1, i) => {
            this.particles.slice(i + 1).forEach(p2 => {
                const dx = p1.x - p2.x;
                const dy = p1.y - p2.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < connectionDistance) {
                    const opacity = (1 - dist / connectionDistance) * baseOpacity;
                    this.ctx.beginPath();
                    this.ctx.moveTo(p1.x, p1.y);
                    this.ctx.lineTo(p2.x, p2.y);
                    this.ctx.strokeStyle = `rgba(${strokeColor}, ${opacity})`;
                    this.ctx.lineWidth = 0.5;
                    this.ctx.stroke();
                }
            });
        });
    }

    animate() {
        this.ctx.clearRect(0, 0, this.width, this.height);
        this.drawGrid();
        this.drawConnections();
        this.drawParticles();
        requestAnimationFrame(() => this.animate());
    }
}


